import { BehaviorSubject, defer, MonoTypeOperatorFunction } from 'rxjs';
import { finalize } from 'rxjs/operators';

/**
 * Custom operator to control loading state either reactively or imperatively.
 *
 * - Sets loading to true when the observable starts.
 * - Sets loading to false when it completes or errors.
 * - Supports multiple concurrent calls with internal reference counting.
 *
 * Accepts:
 *  - a BehaviorSubject<boolean>
 *  - or a function (isLoading: boolean) => void
 *
 * Example:
 *   .pipe(withLoading(this.saving$))
 *   .pipe(withLoading(v => this.saving = v))
 */
export function withLoading<T>(
	loadingTarget: BehaviorSubject<boolean> | ((loading: boolean) => void)
): MonoTypeOperatorFunction<T> {
	let activeCount = 0;

	const setLoading = (value: boolean): void => {
		if (loadingTarget instanceof BehaviorSubject) {
			loadingTarget.next(value);
		} else {
			loadingTarget(value);
		}
	};

	const startLoading = (): void => {
		activeCount++;
		if (activeCount === 1) {
			setLoading(true);
		}
	};

	const stopLoading = (): void => {
		activeCount = Math.max(0, activeCount - 1);
		if (activeCount === 0) {
			setLoading(false);
		}
	};

	return source$ =>
		defer(() => {
			startLoading();
			return source$.pipe(finalize(stopLoading));
		});
}
